import { ValuationType } from '@aviv-estimation-funnel/estimation-data-models/enums/valuation-type.enum';
import { TLocale } from '@aviv-estimation-funnel/shells/src/app/helpers/i18n';
import { Box } from '@gemini/core';
import { CommentIcon } from '@gemini/icons';
import { Link, Modal, useSnackbar } from '@gemini/ui';
import { addError } from '@mfe-helpers/datadog';
import formatNumberToCurrency from '@mfe-helpers/formatNumberToCurrency';
import EstimateFeedbackForm from '@mfe-presentation/components/EstimateFeedbackForm';
import { useAppContext, useAppDispatch } from '@mfe-presentation/contexts';
import { estimationFeedback } from '@mfe-services/EstimationFeedbackRequest';
import { EActionType } from '@mfe-types/context';
import { useEffect } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useLocale, useTranslation } from '@aviv-unified-frontend/runtime/i18n';
import { NAME_SPACE } from '@mfe-presentation/hooks/useLoadTranslations';

const EstimateFeedbackModal = ({ type }: { type: ValuationType }) => {
    const { estimations } = useAppContext();
    const { t } = useTranslation(NAME_SPACE);
    const { error: errorSnackbar, success: successSnackbar } = useSnackbar();
    const dispatch = useAppDispatch();
    const locale = useLocale();

    const price = formatNumberToCurrency(
        type === ValuationType.Rent
            ? estimations.valuation.rentPrice
            : estimations.valuation.sellPrice,
        locale as unknown as TLocale,
    );

    const formResolver: any = async (fieldValues: any) => {
        return {
            values: fieldValues,
            errors: {
                estimateFeedbackValue: estimateFeedbackValue
                    ? {}
                    : {
                          type: 'required',
                          types: [
                              {
                                  code: 'required',
                              },
                          ],
                      },
            },
        };
    };

    const formProviderMethods = useForm({
        mode: 'all',
        resolver: async (values) => formResolver(values),
    });

    const estimateFeedbackValue = useWatch({
        control: formProviderMethods.control,
        name: 'estimateFeedbackValue',
    });

    useEffect(() => {
        if (estimateFeedbackValue) {
            formProviderMethods.trigger();
        }
    }, [estimateFeedbackValue]);

    const handleSubmit = async (close: () => void) => {
        formProviderMethods.trigger();

        const { estimateFeedbackValue, estimateFeedbackExpectedPrice, estimateFeedbackReason } =
            formProviderMethods.getValues();

        dispatch({
            type: EActionType.SetHasSubmittedPriceEstimateFeedback,
            payload: true,
        });

        if (!estimateFeedbackValue) {
            return;
        }

        try {
            await estimationFeedback(estimations.id, type, {
                value: estimateFeedbackValue,
                expectedPrice: estimateFeedbackExpectedPrice,
                reason: estimateFeedbackReason,
            });
            close();
            successSnackbar({
                description: t('step_results.resultCard.estimateFeedback.snackbar.success'),
                variant: 'success',
                testId: 'success-snackbar',
            });
            formProviderMethods.resetField('estimateFeedbackValue');
            formProviderMethods.resetField('estimateFeedbackExpectedPrice');
            formProviderMethods.resetField('estimateFeedbackReason');
        } catch (error: any) {
            close();
            addError(error);
            errorSnackbar({
                description: t('step_results.resultCard.estimateFeedback.snackbar.error'),
                variant: 'error',
                testId: 'error-snackbar',
            });
            dispatch({
                type: EActionType.SetHasSubmittedPriceEstimateFeedback,
                payload: false,
            });
        }
    };

    return (
        <Modal
            title={t(`step_results.resultCard.estimateFeedback.modal.title.${type.toLowerCase()}`, {
                replace: { price: price ?? '' },
            })}
            isDismissable
            footerStartButtonProps={(close: () => void) => ({
                children: t(`step_results.resultCard.estimateFeedback.modal.button.discard`),
                onPress: () => close(),
            })}
            footerEndButtonProps={(close: () => void) => ({
                children: t(`step_results.resultCard.estimateFeedback.modal.button.submit`),
                onPress: async () => handleSubmit(close),
            })}
            trigger={(props) => (
                <Box typography="typography.body.16.regular">
                    {/* @ts-expect-error no href */}
                    <Link {...props} icon={CommentIcon} variant="inline">
                        {t(`step_results.resultCard.estimateFeedback.title`)}
                    </Link>
                </Box>
            )}
        >
            <Box display="flex" flexDirection="column" alignItems="center">
                <Box maxWidth="320px">
                    <Box marginBottom="spacing.24" typography="typography.body.14.regular">
                        {t(`step_results.resultCard.estimateFeedback.modal.subtitle`)}
                    </Box>
                    <FormProvider {...formProviderMethods}>
                        <EstimateFeedbackForm />
                    </FormProvider>
                </Box>
            </Box>
        </Modal>
    );
};

export default EstimateFeedbackModal;
